import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { BrandLockup } from "@/components/layout/BrandLockup";
import { authService } from "@/lib/auth/authService";
import { supabase } from "@/lib/supabase/client";

type Status = "checking" | "ready" | "loading" | "success" | "invalid";

type FieldErrors = {
  password?: string;
  confirmPassword?: string;
  form?: string;
};

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>("checking");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  // When Supabase redirects back after the reset email link is clicked,
  // detectSessionInUrl:true exchanges the token immediately on client init —
  // often before this component mounts. So we do TWO checks:
  //   1. getSession() on mount — catches tokens already exchanged
  //   2. onAuthStateChange listener — catches tokens exchanged after mount
  useEffect(() => {
    let settled = false;

    // Check 1: session may already exist (token was processed before mount)
    void supabase.auth.getSession().then(({ data }) => {
      if (settled) return;
      // A recovery session has a user but was triggered by PASSWORD_RECOVERY
      // We detect it by checking if the URL still contains the token params
      // or simply by the presence of a fresh session on this route.
      if (data.session?.user) {
        settled = true;
        setStatus("ready");
      }
    });

    // Check 2: event fires after mount (slower networks / PKCE code exchange)
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" && !settled) {
        settled = true;
        setStatus("ready");
      }
    });

    // Fallback: if neither check resolves within 8 s, the link is genuinely bad
    const fallback = setTimeout(() => {
      if (!settled) setStatus("invalid");
    }, 8000);

    return () => {
      data.subscription.unsubscribe();
      clearTimeout(fallback);
    };
  }, []);

  // After a successful password update, wait for Supabase to confirm the
  // session is live before navigating — prevents ProtectedRoute from bouncing
  // back to /login. Properly cleaned up on unmount to avoid listener leaks.
  useEffect(() => {
    if (status !== "success") return;

    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === "USER_UPDATED" || event === "SIGNED_IN") {
        navigate("/dashboard");
      }
    });

    const fallback = setTimeout(() => {
      navigate("/dashboard");
    }, 4000);

    return () => {
      data.subscription.unsubscribe();
      clearTimeout(fallback);
    };
  }, [status, navigate]);

  function validate(): FieldErrors {
    const errors: FieldErrors = {};
    if (!password) {
      errors.password = "New password is required.";
    } else if (password.length < 8) {
      errors.password = "Password must be at least 8 characters.";
    }
    if (!confirmPassword) {
      errors.confirmPassword = "Please confirm your new password.";
    } else if (confirmPassword !== password) {
      errors.confirmPassword = "Passwords do not match.";
    }
    return errors;
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setStatus("loading");

    const result = await authService.updatePassword(password);

    if (result.success) {
      setStatus("success");
      // Navigation is handled by the useEffect below that watches status === "success"
    } else {
      setStatus("ready");
      setFieldErrors({
        form: result.error ?? "Failed to update password. Please try again.",
      });
    }
  };

  const fieldClass = (hasError: boolean) =>
    `w-full px-4 py-3 bg-zinc-900 border rounded-lg focus:ring-2 focus:border-transparent text-white placeholder-zinc-500 transition-colors ${
      hasError
        ? "border-red-500 focus:ring-red-500/40"
        : "border-zinc-800 focus:ring-[rgb(var(--cp)/0.70)]"
    }`;

  // ── Checking for recovery session ─────────────────────────────────────────
  if (status === "checking") {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-zinc-950 px-4 py-8">
        <div className="flex flex-col items-center gap-4 text-zinc-400">
          <svg className="w-8 h-8 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          <p className="text-sm">Verifying reset link…</p>
        </div>
      </div>
    );
  }

  // ── Invalid / expired link ────────────────────────────────────────────────
  if (status === "invalid") {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-zinc-950 px-4 py-8 sm:px-6">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/25 flex items-center justify-center mx-auto mb-6">
            <svg
              className="w-8 h-8 text-red-400"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
              />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">Link expired</h1>
          <p className="text-zinc-400 mb-8">
            This password reset link is invalid or has expired. Reset links are
            only valid for 1 hour.
          </p>
          <Link
            to="/forgot-password"
            className="block w-full px-4 py-3 bg-[rgb(var(--cp))] hover:bg-[rgb(var(--cs))] text-white font-bold rounded-lg transition-all text-center"
          >
            Request a New Link
          </Link>
          <div className="mt-4">
            <Link
              to="/login"
              className="text-sm text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ── Success ────────────────────────────────────────────────────────────────
  if (status === "success") {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-zinc-950 px-4 py-8 sm:px-6">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-[rgb(var(--cp)/0.15)] border border-[rgb(var(--cp)/0.30)] flex items-center justify-center mx-auto mb-6">
            <svg
              className="w-8 h-8 text-[rgb(var(--cp))]"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m4.5 12.75 6 6 9-13.5"
              />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">
            Password updated!
          </h1>
          <p className="text-zinc-400">Taking you to your dashboard…</p>
        </div>
      </div>
    );
  }

  // ── Form ───────────────────────────────────────────────────────────────────
  return (
    <div className="flex min-h-[100dvh] overflow-x-hidden overflow-y-auto bg-zinc-950">
      <div className="flex min-w-0 flex-1 items-center justify-center px-4 py-8 sm:px-6 sm:py-12">
        <div className="max-w-md w-full">
          {/* Logo */}
          <div className="mb-12">
            <Link
              to="/"
              className="inline-block hover:opacity-80 transition-opacity"
            >
              <BrandLockup size="xl" />
            </Link>
          </div>

          <div className="mb-8">
            <h1 className="text-4xl font-bold text-white mb-2">
              Set new password
            </h1>
            <p className="text-zinc-400">
              Choose a strong password for your account.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            {/* New password */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-zinc-300 mb-2"
              >
                New Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (fieldErrors.password)
                    setFieldErrors((prev) => ({
                      ...prev,
                      password: undefined,
                    }));
                }}
                className={fieldClass(!!fieldErrors.password)}
                placeholder="••••••••"
                disabled={status === "loading"}
                aria-invalid={!!fieldErrors.password}
                aria-describedby={fieldErrors.password ? "reset-password-error" : "reset-password-hint"}
              />
              {fieldErrors.password ? (
                <p id="reset-password-error" role="alert" className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
                  <svg
                    className="w-3 h-3 shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10A8 8 0 1 1 2 10a8 8 0 0 1 16 0Zm-7 4a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm-1-9a1 1 0 0 0-1 1v4a1 1 0 1 0 2 0V6a1 1 0 0 0-1-1Z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {fieldErrors.password}
                </p>
              ) : (
                <p id="reset-password-hint" className="mt-1.5 text-xs text-zinc-500">
                  Minimum 8 characters.
                </p>
              )}
            </div>

            {/* Confirm password */}
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-zinc-300 mb-2"
              >
                Confirm New Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (fieldErrors.confirmPassword)
                    setFieldErrors((prev) => ({
                      ...prev,
                      confirmPassword: undefined,
                    }));
                }}
                className={fieldClass(!!fieldErrors.confirmPassword)}
                placeholder="••••••••"
                disabled={status === "loading"}
                aria-invalid={!!fieldErrors.confirmPassword}
                aria-describedby={fieldErrors.confirmPassword ? "reset-confirm-error" : undefined}
              />
              {fieldErrors.confirmPassword && (
                <p id="reset-confirm-error" role="alert" className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
                  <svg
                    className="w-3 h-3 shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10A8 8 0 1 1 2 10a8 8 0 0 1 16 0Zm-7 4a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm-1-9a1 1 0 0 0-1 1v4a1 1 0 1 0 2 0V6a1 1 0 0 0-1-1Z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {fieldErrors.confirmPassword}
                </p>
              )}
            </div>

            {/* Form-level error */}
            {fieldErrors.form && (
              <div role="alert" className="p-3.5 bg-red-500/10 border border-red-500/25 rounded-lg text-red-400 text-sm flex items-start gap-2">
                <svg
                  className="w-4 h-4 mt-0.5 shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10A8 8 0 1 1 2 10a8 8 0 0 1 16 0Zm-7 4a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm-1-9a1 1 0 0 0-1 1v4a1 1 0 1 0 2 0V6a1 1 0 0 0-1-1Z"
                    clipRule="evenodd"
                  />
                </svg>
                {fieldErrors.form}
              </div>
            )}

            <button
              type="submit"
              disabled={status === "loading"}
              className="w-full px-4 py-3 bg-[rgb(var(--cp))] hover:bg-[rgb(var(--cs))] disabled:bg-zinc-700 text-white font-bold rounded-lg transition-all"
            >
              {status === "loading"
                ? "Updating password..."
                : "Update Password"}
            </button>
          </form>
        </div>
      </div>

      {/* Right Side */}
      <div className="hidden lg:block flex-1 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900/50 to-black/50 z-10" />
        <img
          src="https://images.unsplash.com/photo-1758957097813-9b391db18caa?w=1200&q=80"
          alt="Cyclist training"
          className="w-full h-full object-cover"
        />
        <div className="absolute bottom-12 left-12 right-12 z-20 text-white">
          <h2 className="text-3xl font-bold mb-2">Back on the Bike</h2>
          <p className="text-zinc-300 text-lg">
            Set your new password and get back to training.
          </p>
        </div>
      </div>
    </div>
  );
}
